import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useDayClosingServiceInvoiceAPI } from "@/app/hooks/api_hooks/dayClosing/useDayClosingServiceInvoiceAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";

const ServiceInvoicesClosingComponent = () => {
  const { execute: fetchServiceInvoiceData, serviceInvoicesData } =
    useDayClosingServiceInvoiceAPI();

  const queryParams = new URLSearchParams(location.search);
  const isLoading = useLoader("isClosingDataInProgress");
  const { NO_APPOINTMENTS } = useImageConstants();
  const { getRouteKey } = useRouter();
  const { veticDateFormat } = useUtils();
  const navigate = useNavigate();

  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process") ?? "";
  const queryCity = queryParams.get("city_id");
  const time = queryParams.get("t") ?? "";
  const lastRequestKeyRef = useRef("");

  useEffect(() => {
    if (!queryClinics) return;
    const requestKey = `${queryClinics}-${queryShift}-${processId}-${queryCity}-${time}`;
    if (lastRequestKeyRef.current === requestKey) return;
    lastRequestKeyRef.current = requestKey;
    fetchServiceInvoiceData(queryClinics, queryShift);
  }, [fetchServiceInvoiceData, processId, queryCity, queryClinics, queryShift, time]);

  const serviceInvoices = serviceInvoicesData?.filter((clinic) => clinic?.closure_list?.length > 0) || [];

  const handleCreateAppointment = (invoice: any, selectedPet?: any) => {
    const userId = invoice?.customer_details?.id || invoice?.user_details?.id || invoice?.user_id;
    const petId = selectedPet?.id || selectedPet?.pet_id || "";
    if (!userId) return;
    navigate(`${getRouteKey("AVAILABLE_SLOTS", "url")}/${userId}${petId ? `/${petId}` : ""}`);
  };
  return (
    <div className="w-full">
      {isLoading ? (
        <TableSkeletonComponent columnLength={8} />
      ) : serviceInvoices?.length > 0 ? (
        <div className="space-y-3 overflow-y-auto max-h-[650px]">
          {serviceInvoices?.map((serviceInvoicesData: any, clinicIndex: number) => (
            <div key={clinicIndex}
              className="rounded-2xl border border-[#ECEEF2] bg-white p-3 shadow-sm"
            >
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal">
                    <tr>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        User Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Pet Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Clinic Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Line Item Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Invoice Date & Time
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-xs">
                    {serviceInvoicesData?.closure_list?.flatMap((invoice: any) => {
                        const petList = Array.isArray(invoice?.pet_details) ? invoice?.pet_details : [];
                        if (petList.length === 0) {
                          return [{ invoice, pet: null }];
                        }
                        return petList.map((pet: any) => ({ invoice, pet }));
                      })?.map(
                        (row: any, rowIndex: number, arr: any[]
                        ) => {
                          const invoiceData = row.invoice;
                          const pet = row.pet;
                          const isFirst = rowIndex === 0;
                          const isLast = rowIndex === arr.length - 1;
                          const isSingle = arr.length === 1;
                          const itemNameValue = invoiceData?.discrepant_pet_service_data?.[0]?.sub_service_name || "-";

                          return (
                            <tr
                              key={`${invoiceData?.invoice_id}-${pet?.id || pet?.pet_id || rowIndex}`}
                              className={`
                              bg-white hover:bg-[#FCFCFD] transition-colors
                              ${isSingle ? "rounded-xl" : ""}
                              ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                              ${isLast && !isSingle ? "rounded-b-xl" : ""}
                            `}
                            >
                              <td
                                className={`
                                p-3 text-xs whitespace-nowrap
                                ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                              `}
                              >
                                {invoiceData?.customer_details?.name || "-"}
                              </td>

                              <td className="p-3 text-xs whitespace-nowrap">
                                {pet?.name || "-"}
                              </td>

                              <td className="p-3 text-xs whitespace-nowrap">
                                {invoiceData?.clinic_details?.name || serviceInvoicesData?.clinic_details?.clinic_name || "-"}
                              </td>

                              <td className="p-3 text-xs whitespace-nowrap">
                                {itemNameValue}
                              </td>

                              <td className="p-3 text-xs whitespace-nowrap">
                                {invoiceData?.created_at ? veticDateFormat(invoiceData?.created_at) : "-"}
                              </td>

                              <td
                                className={`
                                p-3 text-xs whitespace-nowrap
                                ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                              `}
                              >
                                {!invoiceData?.closure_status ? (
                                  <button
                                    type="button"
                                    onClick={() => handleCreateAppointment(invoiceData, pet)}
                                    className="bg-black px-4 py-2 rounded-lg text-xs text-white font-medium whitespace-nowrap hover:opacity-90 transition-opacity"
                                  >
                                    Create Appointment
                                  </button>
                                ) : ("-")}
                              </td>
                            </tr>
                          );
                        }
                      )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <NoDataFoundComponent
          imageURL={NO_APPOINTMENTS}
          message="No Service Invoices Found"
        />
      )}
    </div>
  );
};

export default ServiceInvoicesClosingComponent;
